import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, Facebook, Instagram } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — SCU will reply soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-scu-obsidian text-white">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20">
          <div className="text-xs uppercase tracking-[0.35em] text-scu-electric">Contact</div>
          <h1 className="mt-3 font-bebas text-6xl md:text-8xl tracking-wider">Get In Touch</h1>
        </div>
        <div className="stripe-divider" />
      </section>

      <section className="mx-auto max-w-[1200px] px-4 lg:px-8 py-16 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-scu-purple font-bold">Reach SCU</div>
            <h2 className="font-bebas text-3xl tracking-wide text-scu-obsidian mt-1">Contact Information</h2>
          </div>
          <div className="space-y-3 text-scu-smoke">
            <div className="flex items-start gap-3">
              <Mail className="h-4 w-4 text-scu-purple mt-1 flex-shrink-0" />
              <a data-testid="contact-email" href="mailto:StCharlesUnited26@gmail.com" className="hover:text-scu-purple break-all">StCharlesUnited26@gmail.com</a>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="h-4 w-4 text-scu-purple mt-1 flex-shrink-0" />
              <div className="flex flex-col gap-1">
                <div>
                  <a data-testid="contact-phone-1" href="tel:+19852103113" className="hover:text-scu-purple font-bold">(985) 210-3113</a>
                  <span className="text-xs text-scu-smoke/70 ml-2">Reginald Johnson</span>
                </div>
                <div>
                  <a data-testid="contact-phone-2" href="tel:+13465819282" className="hover:text-scu-purple font-bold">(346) 581-9282</a>
                  <span className="text-xs text-scu-smoke/70 ml-2">Shelby Boyle</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 pt-2">
              <a data-testid="contact-facebook" href="https://www.facebook.com/share/1EwQGgCgPR/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-scu-smoke hover:text-scu-purple transition-colors"><Facebook className="h-5 w-5" /></a>
              <a data-testid="contact-instagram" href="https://www.instagram.com/stcharlesunitedfb?igsh=ZDMyMjVrMWpjemd6" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-scu-smoke hover:text-scu-purple transition-colors"><Instagram className="h-5 w-5" /></a>
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="md:col-span-3 bg-white border border-scu-smoke/15 rounded-md p-6 md:p-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <F label="Name"><Input data-testid="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Email"><Input data-testid="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
            <F label="Phone"><Input data-testid="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
            <F label="Subject"><Input data-testid="c-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></F>
          </div>
          <F label="Message"><Textarea data-testid="c-msg" rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></F>
          <Button data-testid="c-submit" type="submit" disabled={loading} className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest h-11 px-6">
            {loading ? "Sending…" : "Send Message"}
          </Button>
        </form>
      </section>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">{label}</Label>
      {children}
    </div>
  );
}
